// Pure transform from normalized bucket rows to box-whisker chart data.
//
// Box = p25-p75, whiskers = p05-p95 (robust: one outlier no longer stretches
// the panel), median tick; exact min/max travel along for the tooltip. Rows
// whose start falls before the window (the leading partial bucket the server
// returns because a later trade is in-window) are dropped -- they hang off the
// fixed left axis and would push the populated count past the nominal bucket
// count. The denominator is guarded so populated can never exceed expected.

export interface BoxRow {
  bucket: string;
  median_bps: number | null;
  p25_bps: number | null;
  p75_bps: number | null;
  p05_bps: number | null;
  p95_bps: number | null;
  min_bps: number | null;
  max_bps: number | null;
  cov: number;
  total: number;
}

// [tsMs, p05, p25, median, p75, p95, min, max, cov, total]
export type BoxDatum = [number, number, number, number, number, number, number, number, number, number];

export interface BoxSeries {
  data: BoxDatum[];
  populatedBuckets: number;
  tradedBuckets: number;
  expectedBuckets: number;
  windowStartMs: number;
  windowEndMs: number;
}

export function expectedBucketCount(lookbackMinutes: number, bucketSeconds: number): number {
  return Math.max(1, Math.floor((lookbackMinutes * 60) / bucketSeconds));
}

export interface BoxWindow {
  lookbackMinutes: number;
  bucketSeconds: number;
  nowMs: number;
}

export function toBoxSeries(rows: BoxRow[], w: BoxWindow): BoxSeries {
  const windowStartMs = w.nowMs - w.lookbackMinutes * 60_000;
  const windowEndMs = w.nowMs;
  const sorted = [...rows].sort((a, b) => Date.parse(a.bucket) - Date.parse(b.bucket));

  const data: BoxDatum[] = [];
  let traded = 0;
  for (const r of sorted) {
    const ts = Date.parse(r.bucket);
    if (ts < windowStartMs) continue; // leading partial: outside the window
    if (r.total > 0) traded++;
    if (
      r.median_bps == null ||
      r.p25_bps == null ||
      r.p75_bps == null ||
      r.p05_bps == null ||
      r.p95_bps == null ||
      r.min_bps == null ||
      r.max_bps == null
    ) {
      continue;
    }
    data.push([
      ts,
      r.p05_bps,
      r.p25_bps,
      r.median_bps,
      r.p75_bps,
      r.p95_bps,
      r.min_bps,
      r.max_bps,
      r.cov,
      r.total,
    ]);
  }

  // Guard: populated can never exceed expected (an exact bucket-boundary `now`
  // can otherwise yield one extra slot). Normal case shows the nominal count.
  const nominal = expectedBucketCount(w.lookbackMinutes, w.bucketSeconds);
  return {
    data,
    populatedBuckets: data.length,
    tradedBuckets: traded,
    expectedBuckets: Math.max(nominal, data.length),
    windowStartMs,
    windowEndMs,
  };
}

export function allUnusable(s: BoxSeries): boolean {
  return s.tradedBuckets > 0 && s.populatedBuckets === 0;
}
